import { useState, useCallback, useEffect, useRef } from "react";
import { apiFetch } from "../../../../api";
import { trackEvent } from "../../../../analytics/posthog";

// Lightweight event bus so the sidebar badge can react to word save/remove
const listeners = new Set();
export function onWordSaveChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyWordSaveChange() {
  listeners.forEach((fn) => fn());
}

export function useWordSave(storyId, initialSavedIds = []) {
  const [savedWordIds, setSavedWordIds] = useState(() => new Set(initialSavedIds.map(String)));
  const [pendingWordId, setPendingWordId] = useState(null);
  const [sparkleWordId, setSparkleWordId] = useState(null);
  const sparkleTimer = useRef(null);

  // Sync when initialSavedIds loads asynchronously (e.g. after fetch)
  const prevLen = useRef(0);
  useEffect(() => {
    if (initialSavedIds.length > 0 && prevLen.current === 0) {
      setSavedWordIds(new Set(initialSavedIds.map(String)));
    }
    prevLen.current = initialSavedIds.length;
  }, [initialSavedIds]);

  useEffect(() => () => clearTimeout(sparkleTimer.current), []);

  const saveWord = useCallback(async (wordId, surface) => {
    if (!storyId || !wordId) return;
    const key = String(wordId);
    setSavedWordIds((prev) => new Set([...prev, key]));
    setPendingWordId(key);

    // Trigger sparkle
    setSparkleWordId(key);
    clearTimeout(sparkleTimer.current);
    sparkleTimer.current = setTimeout(() => setSparkleWordId(null), 700);

    trackEvent("word_saved", { story_id: storyId, word_id: wordId, surface });
    try {
      const res = await apiFetch(
        `/api/v1/dashboard/stories/${storyId}/words/${wordId}/save`,
        { method: "POST" },
      );
      if (!res.ok) {
        setSavedWordIds((prev) => { const s = new Set(prev); s.delete(key); return s; });
      } else {
        notifyWordSaveChange();
      }
    } catch {
      setSavedWordIds((prev) => { const s = new Set(prev); s.delete(key); return s; });
    } finally {
      setPendingWordId(null);
    }
  }, [storyId]);

  const removeWord = useCallback(async (wordId, surface) => {
    if (!storyId || !wordId) return;
    const key = String(wordId);
    setSavedWordIds((prev) => { const s = new Set(prev); s.delete(key); return s; });
    setPendingWordId(key);
    trackEvent("word_removed", { story_id: storyId, word_id: wordId, surface });
    try {
      const res = await apiFetch(
        `/api/v1/dashboard/stories/${storyId}/words/${wordId}/save`,
        { method: "DELETE" },
      );
      if (!res.ok) {
        setSavedWordIds((prev) => new Set([...prev, key]));
      } else {
        notifyWordSaveChange();
      }
    } catch {
      setSavedWordIds((prev) => new Set([...prev, key]));
    } finally {
      setPendingWordId(null);
    }
  }, [storyId]);

  const isSaved = useCallback((wordId) => savedWordIds.has(String(wordId)), [savedWordIds]);

  return { isSaved, saveWord, removeWord, pendingWordId, sparkleWordId, savedCount: savedWordIds.size };
}
